import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createCommentRuleApi,
  deleteCommentRuleApi,
  getCommentRuleApi,
  getCommentRulesApi,
  previewCommentRuleApi,
  updateCommentRuleApi,
} from "../../api/ecommerce/CommentRuleApi";

const initialState = {
  commentRules: [],
  commentRule: null,
  createdCommentRule: null,
  updatedCommentRule: null,
  deletedCommentRule: null,
  preview: null,
  listing: false,
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  previewing: false,
  error: null,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

export const getCommentRules = createAsyncThunk(
  "commentRule/getCommentRules",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getCommentRulesApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getCommentRule = createAsyncThunk(
  "commentRule/getCommentRule",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getCommentRuleApi(id);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

// Pass the full configuration body, keeping both platform post IDs as strings.
export const createCommentRule = createAsyncThunk(
  "commentRule/createCommentRule",
  async (body, { rejectWithValue }) => {
    try {
      const response = await createCommentRuleApi(body);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

// Dispatch with { id, body }, where body is the full configuration.
export const updateCommentRule = createAsyncThunk(
  "commentRule/updateCommentRule",
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await updateCommentRuleApi(id, body);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const deleteCommentRule = createAsyncThunk(
  "commentRule/deleteCommentRule",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteCommentRuleApi(id);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

// Dispatch with { configuration, comment }.
export const previewCommentRule = createAsyncThunk(
  "commentRule/previewCommentRule",
  async (body, { rejectWithValue }) => {
    try {
      const response = await previewCommentRuleApi(body);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

const commentRuleSlice = createSlice({
  name: "commentRule",
  initialState,
  reducers: {
    clearCommentRuleError: (state) => {
      state.error = null;
    },
    clearCommentRuleState: () => initialState,
  },
  extraReducers: (builder) => {
    const operations = [
      [getCommentRules, "commentRules", "listing"],
      [getCommentRule, "commentRule", "loading"],
      [createCommentRule, "createdCommentRule", "creating"],
      [updateCommentRule, "updatedCommentRule", "updating"],
      [deleteCommentRule, "deletedCommentRule", "deleting"],
      [previewCommentRule, "preview", "previewing"],
    ];

    operations.forEach(([thunk, dataKey, loadingKey]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[loadingKey] = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[loadingKey] = false;
          state[dataKey] = action.payload?.response ?? action.payload;
        })
        .addCase(thunk.rejected, (state, action) => {
          state[loadingKey] = false;
          state.error =
            action.payload?.errorMessage ||
            action.error?.message ||
            "Something went wrong";
        });
    });
  },
});

export const { clearCommentRuleError, clearCommentRuleState } =
  commentRuleSlice.actions;

export default commentRuleSlice.reducer;
